import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadColumnPreferences } from '../../store/slices/leadsSlice';

// Define required fields constant - update to match new requirements
const REQUIRED_FIELDS = ['leadContact.name', 'leadContact.email', 'address.city'];

function LeadsCustomizeFieldsDialog({ isOpen, onClose, columns = [], onToggleColumn = () => {}, onApply = () => {} }) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedColumns, setSelectedColumns] = React.useState(columns);
  const dispatch = useDispatch();
  const { loading, columnPreferences } = useSelector(state => state.leads);

  // Load preferences when dialog mounts
  React.useEffect(() => {
    if (isOpen && !loading && !columnPreferences?.length) {
      dispatch(loadColumnPreferences());
    }
  }, [dispatch, isOpen, loading, columnPreferences]);

  // Debug toggle function
  const handleToggle = (columnId) => {
    // Don't allow unchecking required fields
    const column = selectedColumns.find(col => col.id === columnId);
    if (column && REQUIRED_FIELDS.includes(column.key) && column.checked) {
      return; // Prevent unchecking required fields
    }
    
    setSelectedColumns(prevColumns => {
      const updated = prevColumns.map(col => {
        if (col.id === columnId) {
          return {
            ...col,
            checked: !col.checked
          };
        }
        return col;
      });
      return updated;
    });
  };

  // Update selected columns when preferences or columns prop changes
  React.useEffect(() => {
    if (columnPreferences?.length) {
      const updatedColumns = columns.map(col => {
        // Always keep required fields checked
        if (REQUIRED_FIELDS.includes(col.key)) {
          return {
            ...col,
            checked: true,
            active: true
          };
        }
        
        const savedPref = columnPreferences.find(pref => parseInt(pref.id) === col.id);
        if (savedPref) {
          return {
            ...col,
            checked: savedPref.checked,
            active: savedPref.active
          };
        }
        return col;
      });
      console.log('Setting initial columns:', updatedColumns);
      setSelectedColumns(updatedColumns);
    } else {
      // Ensure required fields are always checked
      const initialColumns = columns.map(col => ({
        ...col,
        checked: REQUIRED_FIELDS.includes(col.key) ? true : col.checked
      }));
      setSelectedColumns(initialColumns);
    }
  }, [columns, columnPreferences]);

 

  if (!isOpen) return null;

  const handleApply = async () => {
    try {
      // Format columns for API while preserving all necessary properties
      const formattedColumns = selectedColumns.map(({ id, name, checked, active, key, label }) => ({
        id: id.toString(),
        name,
        checked: REQUIRED_FIELDS.includes(key) ? true : checked, // Ensure required fields stay checked
        active,
        key,
        label // Preserve the label property
      }));
      await onApply(formattedColumns);
      onClose();
    } catch (error) {
      console.error('Failed to apply column preferences:', error);
    }
  };

  const handleCancel = () => {
    // Reset to saved preferences or original columns
    if (columnPreferences.length) {
      const updatedColumns = columns.map(col => {
        // Always keep required fields checked
        if (REQUIRED_FIELDS.includes(col.key)) {
          return { ...col, checked: true, active: true };
        }
        
        const savedPref = columnPreferences.find(pref => parseInt(pref.id) === col.id);
        return savedPref ? { ...col, ...savedPref } : col;
      });
      setSelectedColumns(updatedColumns);
    } else {
      // Ensure required fields are always checked
      const resetColumns = columns.map(col => ({
        ...col,
        checked: REQUIRED_FIELDS.includes(col.key) ? true : col.checked
      }));
      setSelectedColumns(resetColumns);
    }
    onClose();
  };

  // Filter columns based on search
  const filteredColumns = selectedColumns.filter(column =>
    column.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log('Rendering Dialog with Filtered Columns:', filteredColumns);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-xl w-[80%] max-w-4xl min-w-[420px]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Customize Lead Fields</h2>
          <button 
            className="text-gray-400 hover:text-gray-500"
            onClick={handleCancel}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search fields..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Fields marked with <span className="text-red-500">*</span> are required for creating leads.
          </div>
        </div>

        {/* Column List */}
        <div className="max-h-[460px] overflow-y-auto">
          {filteredColumns.map((column) => (
            <div key={column.id} className="flex items-center px-4 py-3 border-b border-gray-100 hover:bg-gray-50">
              <div className="flex items-center flex-1">
                <input 
                  type="checkbox" 
                  checked={!!column.checked}
                  onChange={() => handleToggle(column.id)}
                  disabled={REQUIRED_FIELDS.includes(column.key)} // Disable checkboxes for required fields
                  className={`w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                    REQUIRED_FIELDS.includes(column.key) ? 'cursor-not-allowed opacity-60' : ''
                  }`}
                />
                <span className="ml-3 text-sm text-gray-900">
                  {column.name}
                  {REQUIRED_FIELDS.includes(column.key) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </span>
              </div>
              <div className={`text-xs px-2 py-1 rounded-full ${
                column.active ? 'text-green-700 bg-green-50' : 'text-gray-600 bg-gray-50'
              }`}>
                {REQUIRED_FIELDS.includes(column.key) ? 'Required' : column.active ? 'Active' : 'Inactive'}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LeadsCustomizeFieldsDialog; 